/**
 * English base dictionary. Every other language is checked against these keys,
 * and any key a translation is missing falls back to the English string here —
 * a missing translation shows real English text, never a raw key.
 *
 * Scope is deliberate: the student-facing surfaces (navigation, dashboard,
 * doubts, exams, results, profile) plus shared actions. Admin/management
 * screens are not translated yet — see `TRANSLATION_SCOPE_NOTE`.
 *
 * Keys are dotted and grouped by surface. Interpolation uses {braces}.
 */

export const en = {
    // ---- shared actions & states -------------------------------------------
    "library.readingTools": "Reading tools",
    "common.save": "Save",
    "common.cancel": "Cancel",
    "common.close": "Close",
    "linkViewer.openNewTab": "Open in new tab",
    "linkViewer.blankHint": "Some websites do not allow being shown inside another app. If this stays blank, use Open in new tab.",
    "linkViewer.imageFailed": "This image could not be loaded here. Use Open in new tab.",
    "common.retry": "Try again",
    "common.loading": "Loading…",
    "common.saving": "Saving…",
    "common.search": "Search",
    "common.back": "Back",
    "common.continue": "Continue",
    "common.optional": "optional",
    "common.logout": "Log out",
    "common.none": "Nothing here yet",

    // ---- navigation --------------------------------------------------------
    "nav.dashboard": "Dashboard",
    "nav.myExams": "My exams",
    "nav.myResults": "My results",
    "nav.doubts": "Ask AI",
    "nav.practicePapers": "Practice Papers",
    "nav.myPapers": "My papers",
    "nav.buildPaper": "Build a paper",
    "nav.aiModels": "AI models",
    "nav.profile": "Profile & settings",
    "nav.analytics": "Analytics",
    "nav.manageAdmin": "Manage Admin",
    "nav.manageCoachings": "Manage Coachings",
    "nav.manageSubjects": "Manage Subjects",
    "nav.manageExams": "Manage Exams",
    "nav.manageQuiz": "Manage Quiz",
    "nav.manageStudents": "Manage Students",
    "nav.manageReviews": "Manage Reviews",
    "nav.manageAI": "Manage AI",
    "nav.listView": "List / View",
    "nav.coachingHubs": "Coaching Hubs",
    "nav.subjectMatrix": "Subject Matrix",
    "nav.examLibrary": "Exam Library",
    "nav.quizBuilder": "Quiz Builder",
    "nav.translations": "Translations",
    "nav.studentPapers": "Student Papers",
    "nav.studentDirectory": "Student Directory",
    "nav.feedbackCenter": "Feedback Center",
    "nav.workbench": "Workbench",
    "nav.researchStudio": "Research Studio",
    "nav.thinkboard": "Thinkboard",

    // ---- dashboard ---------------------------------------------------------
    "dashboard.welcome": "Welcome back, {name}",
    "dashboard.upcomingExams": "Upcoming exams",
    "dashboard.activeExams": "Live now",
    "dashboard.completed": "Completed",
    "dashboard.streak": "Study streak",
    "dashboard.streakDays": "{count} day streak",
    "dashboard.mastery": "Confidence by subject",

    // ---- doubt solver ------------------------------------------------------
    "doubt.photoLabel": "Attach a photo",
    "doubt.submit": "Get answer",
    "doubt.submitting": "Thinking…",
    "doubt.empty": "You have not asked anything yet.",
    "doubt.you": "You",
    "doubt.tutor": "Tutor",
    "doubt.turnCount": "{count} messages",
    "doubt.awaitingTeacher": "awaiting teacher review",
    "doubt.followUpCta": "Ask a follow-up",
    "doubt.followUpLabel": "Your follow-up",
    "doubt.followUpPlaceholder": "e.g. Why the second step?",
    "doubt.followUpSend": "Send",
    "doubt.stopGenerating": "Stop",
    "doubt.followUpHint": "Your tutor remembers this conversation and replies in {language}.",

    // ---- chat (AI tutor) ---------------------------------------------------
    "chat.newChat": "New chat",
    "chat.build.quiz": "Build a quiz?",
    "chat.build.confirm": "Build it",
    "chat.build.dismiss": "No, just answer me",
    "chat.build.assignment": "Build an assignment?",
    "chat.build.topic": "Topic",
    "chat.build.difficulty": "Difficulty",
    "chat.build.easy": "easy",
    "chat.build.medium": "medium",
    "chat.build.hard": "hard",
    "chat.build.reviewNote": "You will review every question before it saves.",
    "chat.examPause.hint": "Finish or submit your paper and AI help comes straight back.",
    "chat.conversations": "Chats",
    "chat.pastChats": "Past chats",
    "chat.createArtefact": "Create a presentation, mind map or notes",
    "voice.playClip": "Play your recording",
    "voice.pauseClip": "Pause",
    "artefact.title": "Title",
    "chat.artefact.building": "Building your {kind}…",
    "chat.artefact.buildingNote": "This takes a moment. It will open as soon as it is ready.",
    "chat.artefact.note": "You can edit and rebuild it afterwards.",
    "chat.artefact.confirm": "Build it",
    "chat.artefact.dismiss": "No thanks",
    "chat.artefact.format": "Format",
    "chat.artefact.about": "About",
    "chat.artefact.aboutPlaceholder": "What should it cover?",
    "chat.artefact.grounded": "Built from the chapter you have open",
    "chat.artefact.aria": "Build a study artefact",
    "artefact.sectionHeading": "Section heading",
    "artefact.pointsPlaceholder": "One point per line",
    "artefact.sceneLayoutLabel": "Scene {n} layout",
    "artefact.layout.list": "List",
    "artefact.layout.flow": "Flow (A → B → C)",
    "artefact.layout.compare": "Compare (two columns)",
    "artefact.layout.cycle": "Cycle (loop)",
    "artefact.layoutPoints": "This layout needs {min}–{max} points.",
    "artefact.layoutPointsExact": "This layout needs exactly {n} points. Write each as \"Label: detail\" to title its column.",
    "artefact.outlineHint": "Check the outline before building. Fixing it here takes seconds; fixing it afterwards means building the whole thing again.",
    "artefact.addSection": "Add section",
    "artefact.saveOutline": "Save outline",
    "artefact.saving": "Saving…",
    "artefact.build": "Build it",
    "artefact.building": "Building…",
    "artefact.moveUp": "Move section {n} up",
    "artefact.moveDown": "Move section {n} down",
    "artefact.removeSection": "Remove section {n}",
    "artefact.sectionHeadingLabel": "Section {n} heading",
    "artefact.sectionPointsLabel": "Section {n} points",
    "artefact.download": "Download",
    "artefact.downloadImage": "Download image",
    "artefact.preparing": "Preparing…",
    "artefact.editOutline": "Edit outline and rebuild",
    "artefact.hideOutline": "Hide outline",
    "artefact.builtFrom": "Built from {sources}",
    "artefact.notFound": "This artefact does not exist, or it belongs to another institute.",
    "artefact.empty": "Nothing came back for this one. The outline is kept — edit it and build again.",
    "artefact.buildingKind": "Building your {kind}…",
    "artefact.kind.notes": "Revision notes",
    "artefact.kind.flashcards": "Flashcards",
    "artefact.kind.diagram": "Mind map",
    "artefact.kind.infographic": "One-page explainer",
    "artefact.kind.deck": "Slide deck",
    "artefact.kind.document": "Handout",
    "artefact.kind.video": "Video lesson",
    "artefact.cardCount": "Card {n} of {total}",
    "artefact.reveal": "Tap to reveal",
    "artefact.hint": "Hint: {hint}",
    "artefact.previous": "Previous",
    "artefact.next": "Next",
    "artefact.speakerNotes": "Show speaker notes",
    "chat.moreActions": "More actions",
    "chat.placeholder": "Ask anything about your syllabus…",
    "chat.send": "Send",
    "chat.hint": "Enter to send, Shift+Enter for a new line.",
    "chat.copy": "Copy",
    "chat.copied": "Copied",
    "chat.followup.label": "Do more with this answer",
    "chat.followup.simpler": "Explain it more simply",
    "chat.followup.example": "Give me an example",
    "chat.followup.quiz": "Quiz me on this",
    "chat.followup.similar": "Give me a similar question to practise",
    "chat.followup.again": "Answer again",
    /* The chip's FACE. The message it sends is the full sentence above — a
       control the size of a word, carrying a whole question. */
    "chat.followup.simplerShort": "Simpler",
    "chat.followup.exampleShort": "Example",
    "chat.followup.quizShort": "Quiz me",
    "chat.followup.similarShort": "Similar question",
    "chat.teacher.on": "Teacher mode",
    "chat.teacher.end": "End",
    "chat.teacher.startHint": "Switch on teacher mode: hints and checks instead of straight answers",
    "chat.teacher.endHint": "End teacher mode and go back to normal answers",
    "chat.teacher.teachMe": "Teach me this step by step",
    "chat.teacher.hint": "Give me a hint",
    "chat.teacher.solution": "Show full solution",
    "chat.teacher.hintShort": "Hint",
    "chat.teacher.solutionShort": "Full solution",
    "chat.jumpToLatest": "Jump to latest",
    "chat.removeAttachment": "Remove attachment",
    "chat.modelHint": "The AI model that wrote this answer",
    "chat.tools": "Tools",
    "chat.key.onPaid": "Using your paid model",
    "chat.key.onFree": "Using the free local model",
    "chat.key.addKey": "Add a paid model key",
    "chat.key.provider": "Provider",
    "chat.key.model": "Model",
    "chat.key.apiKey": "API key",
    "chat.key.writeOnly": "Stored encrypted. It is never shown again — saving replaces it.",
    "chat.key.allSettings": "All AI model settings",
    "chat.key.save": "Save key",
    "chat.key.saving": "Saving…",
    "chat.key.saved": "Key saved",
    "chat.disclaimer": "Platform AI can make mistakes — check everything.",
    "chat.attach.image": "Upload image",
    "chat.attach.video": "Upload a short video",
    "chat.attach.camera": "Take a photo",
    "chat.attach.audio": "Record audio",
    "chat.attach.title": "Add",
    "chat.key.dialogTitle": "Link a paid AI model",
    "chat.key.step1": "Choose provider and model",
    "chat.key.step1Hint": "Answers will use this instead of the free local model.",
    "chat.key.step2": "Add your API key",
    "chat.key.linked": "Linked — answers use {model}",
    "chat.key.notLinked": "Not linked — answers use the free local model",
    "chat.key.aPaidModel": "your paid model",
    "chat.key.failed": "That key could not be saved.",
    "chat.emptyTitle": "What are you stuck on?",
    "chat.emptySubtitle": "Ask in any language. Type it, say it, or photograph the question.",
    "chat.suggest1": "Explain Newton's laws with an example",
    "chat.suggest2": "How do I solve time and work problems quickly?",
    "chat.suggest3": "Summarise the Fundamental Rights",
    "chat.suggest4": "Give me a trick for percentage questions",

    // ---- what the tutor is doing right now ---------------------------------
    // Each of these names work the server is actually performing and reports as
    // it happens. Nothing here is decorative: a step that did not correspond to
    // real work would teach students the progress panel is theatre, and then
    // the honest steps stop being believed too.
    "chat.phase.understanding": "Reading your question",
    "chat.phase.understandingGrounded": "Reading your chapter",
    "chat.phase.routing": "Asking the {category} tutor",
    "chat.phase.routingPlain": "Picking the right tutor",
    "chat.phase.recalled": "Answered this one before — reusing it",
    "chat.phase.writing": "Writing the answer",
    "chat.phase.elapsed": "{seconds}s",
    "chat.phase.done.question": "Question read",
    "chat.phase.done.chapter": "Chapter read",
    "chat.phase.done.tutor": "{category} tutor",
    "chat.phase.done.tutorPlain": "Tutor chosen",
    "chat.phase.sources": "Using {count} documents from your library",
    "chat.phase.sourcesOne": "Using 1 document from your library",
    "chat.phase.answeredIn": "Answered in {seconds}s",
    "chat.phase.stillWorking": "Still working. Long questions take longer — nothing has gone wrong.",
    // Shown on the composer while an answer is still arriving.
    "chat.busy.placeholder": "Wait for this answer to finish…",
    "chat.busy.hint": "One question at a time. Let this answer finish, or press stop.",

    // ---- worked solutions --------------------------------------------------
    "solution.heading": "Solution",
    "solution.byTeacher": "From your teacher",
    "solution.byAi": "AI written",
    "solution.partlyAi": "Partly AI written",
    "solution.concept": "The concept",
    "solution.trick": "Quick-solve trick",
    "solution.whyEach": "Why each option",
    "solution.youChose": "You chose this",

    // ---- alternative solution methods --------------------------------------
    "methods.heading": "Other ways to solve this",
    "methods.count": "{count} techniques",
    "methods.whyItWorks": "Why this works",
    "methods.useWhen": "Use when",
    "methods.failsWhen": "Careful",
    "methods.byAi": "AI-generated, checked against the answer key",
    "methods.byTeacher": "From your teacher",

    // ---- voice input -------------------------------------------------------
    "voice.record": "Ask by voice",
    "voice.stop": "Stop recording",
    "voice.discard": "Discard",
    "voice.transcribing": "Converting your speech to text…",
    "voice.unsupported": "This browser cannot record audio. Please type your doubt instead.",
    "voice.denied":
        "Microphone blocked. Allow it in browser settings, or type instead.",
    "voice.noDevice": "No microphone was found. Plug one in, or type your doubt instead.",
    "voice.insecure":
        "This browser blocks the microphone on a page whose certificate it does not trust. Open the site over http://localhost, or install the development certificate.",
    "voice.unavailable": "Voice input is not set up on this server yet. Please type your doubt.",
    "voice.autoDetect": "Detect language",
    "voice.spokenLanguage": "Language you will speak in",
    "voice.checkWords": "check the words",

    // ---- read aloud --------------------------------------------------------
    "speak.play": "Read aloud",
    "speak.stop": "Stop reading",
    "speak.noVoice": "No {language} voice on this device, so read-aloud is off.",

    // ---- exams -------------------------------------------------------------
    "exams.title": "My exams",
    "exams.start": "Start",
    "exams.resume": "Resume",
    "exams.closed": "Closed",
    "exams.timeLeft": "Time left",
    "exams.answered": "{answered} of {total} answered",
    "exams.submit": "Submit paper",

    // ---- results -----------------------------------------------------------
    "results.title": "My results",
    "results.score": "Score",
    "results.accuracy": "Accuracy",
    "results.correct": "Correct",
    "results.incorrect": "Incorrect",
    "results.viewDetail": "View details",

    // ---- profile & settings ------------------------------------------------
    "profile.title": "Profile & settings",
    "profile.subtitle": "Your account details and how the site behaves for you.",
    "profile.accountHeading": "Account",
    "profile.name": "Name",
    "profile.email": "Email",
    "profile.mobile": "Mobile",
    "profile.role": "Role",
    "profile.coaching": "Institute",
    "profile.editName": "Change name",
    "profile.nameSaved": "Name updated.",
    "profile.nameTooShort": "Enter your full name.",
    "profile.sessionsHeading": "Signed-in devices",
    "profile.sessionsHelp": "Signed in on a shared or lost device? This signs you out everywhere, including here.",
    "profile.logoutEverywhere": "Log out of all devices",
    "profile.logoutEverywhereConfirm": "Every device, including this one, will need your password again.",
    "profile.languageHeading": "Language",
    "profile.languageHelp":
        "Sets the site language, your answer language, and the read-aloud voice.",
    "profile.languageLabel": "Website language",
    "profile.languageSaved": "Language changed to {language}.",
    "profile.languagePartial":
        "{language} is partly translated ({percent}% done). Anything not yet translated stays in English.",
    "profile.languageAnswerQuality":
        "Answers in Indian languages depend on the AI model in use. The free local model handles English best; for the strongest results in your language, ask your institute to attach a paid model under AI models.",
    "profile.studyHeading": "What you are studying",
    "profile.editStudy": "Update my subjects",

    // ---- errors ------------------------------------------------------------
    "error.generic": "Something went wrong. Please try again.",
    "error.network": "Could not reach the server. Check your connection.",
    "exams.availableTitle": "Available Exams",
    "exams.availableSubtitle": "Live quizzes open for your institute right now.",
    "exams.none": "No exams are live at the moment. Check back soon.",
    "exams.loadError": "Unable to load exams right now. Please refresh.",
    "exams.duration": "Duration",
    "exams.questions": "Questions",
    "exams.closes": "Closes",
    "exams.attempted": "Attempted",
    "results.exam": "Exam",
    "results.status": "Status",
    "results.submitted": "Submitted",
    "results.attempted": "Attempted",
    "results.loadError": "Unable to load your results right now.",
    "result.answerReview": "Answer Review",
    "result.correctAnswer": "Correct answer",
    "result.yourAnswer": "Your answer",
    "result.modelAnswer": "Model answer",
    "result.manualGrading": "Graded manually by your teacher.",
    "result.mentorTitle": "AI Mentor Diagnosis",
    "result.mentorDiagnosis": "Question-by-question diagnosis",
    "result.loadError": "Unable to load this result right now.",
    "home.learningSpace": "Your learning space",
    "home.readyTitle": "Ready to sharpen your rank?",
    "home.availableNow": "Available now",
    "home.liveQuizzes": "Live quizzes",
    "home.recentActivity": "Recent activity",
    "home.latestAttempts": "Your latest attempts",
    "home.notices": "Notices",
    "home.latestAnnouncements": "Latest announcements",

    // ---- Chat scope, practice papers, answer attribution, video player ----
    "chat.scope.chipAria": "Answer source: {label}. Change",
    "chat.scope.chipTitle": "What should the answer come from?",
    "chat.scope.sheetTitle": "Answer from",
    "chat.scope.groupLabel": "Answer source",
    "chat.scope.smart": "Notes and library",
    "chat.scope.smartHint": "Answers from your uploads and the shared library when your question is about them.",
    "chat.scope.own": "My uploads",
    "chat.scope.oneDoc": "Only: {name}",
    "chat.scope.ownHint": "Only files you uploaded. Pick some, or leave none picked to use all of them.",
    "chat.scope.library": "Library by subject",
    "chat.scope.libraryHint": "Material shared with you, optionally for one subject.",
    "chat.scope.general": "AI knowledge (any topic)",
    "chat.scope.generalHint": "Answers any question from the AI's general knowledge. This is the default.",
    "chat.scope.noUploads": "You have not uploaded anything yet.",
    "chat.scope.uploadLink": "Upload study material",
    "chat.scope.noShared": "Nothing has been shared with you yet.",
    "chat.scope.subject": "Subject",
    "chat.scope.allSubjects": "All subjects",
    "chat.scope.uploadsList": "Your uploads",
    "chat.scope.noPracticeHint": "Practice papers need some material, so they are off in this mode.",
    "chat.scope.makePractice": "Make a practice paper",
    "chat.scope.chip.smart": "Notes + library",
    "chat.scope.chip.own": "My uploads",
    "chat.scope.chip.ownCount": "My files ({count})",
    "chat.scope.chip.library": "Library",
    "chat.scope.chip.librarySubject": "Library: {subject}",
    "chat.scope.chip.general": "AI knowledge",
    "chat.scope.emptyPractice": "Make a practice paper from my material",
    "chat.practice.title": "Practice from your material",
    "chat.practice.intro": "A private practice paper from: {scope}. Questions are drawn from across the material, not just the start.",
    "chat.practice.sizeLabel": "Number of questions",
    "chat.practice.size": "{count} questions",
    "chat.practice.create": "Create practice paper",
    "chat.practice.creating": "Preparing {count} questions...",
    "chat.practice.ready": "{count} questions ready.",
    "chat.practice.keyHidden": "The answer key stays hidden until you submit.",
    "chat.practice.keepChatting": "Keep chatting",
    "chat.practice.start": "Start practice test",
    "chat.practice.error": "The practice paper could not be created. Check that the material has readable text and an AI model is available.",
    "chat.source.pages": "p. {pages}",
    "chat.source.notFound": "Not found in your material - general answer",
    "chat.source.notFoundTip": "Nothing in your selected material matched this question",
    "chat.source.tip": "Answered from this document",
    "chat.subject.school": "School subjects",
    "chat.subject.engineering": "Engineering",
    "chat.subject.computer_it": "Computers and IT",
    "chat.subject.medical": "Medical",
    "chat.subject.commerce_management": "Commerce and management",
    "chat.subject.science": "Science",
    "chat.subject.law": "Law",
    "chat.subject.arts_humanities": "Arts and humanities",
    "chat.subject.education": "Education",
    "chat.subject.languages": "Languages",
    "chat.subject.competitive": "Competitive exams",
    "chat.subject.vocational": "Vocational",
    "chat.subject.research": "Research",
    "common.done": "Done",
    "media.largerView": "Larger view",
    "media.playVideo": "Play video: {title}",
    "media.modalNotice": "This video is played from YouTube and may include ads. It is third-party content and is not verified by your institute.",
    "chat.scope.status": "Answers come from: {label}",
    "chat.scope.turnOff": "Turn off",
    "chat.scope.emptyTitle": "Add notes or books to your library first",
    "chat.scope.emptyBody": "This mode answers from your library, and it is empty. Upload your notes or books, then you can ask questions from them.",
    "chat.scope.emptyOwnBody": "You have not uploaded any notes or books yet. Upload some, then you can ask questions from them.",
    "chat.scope.emptyLibraryBody": "Nothing has been shared in the library yet. Upload your own notes or books, or ask your institute to add material.",
    "chat.scope.processing": "Your files are still being prepared. You can ask questions from them in a moment.",
    "chat.scope.emptyGeneral": "Chat without notes",

    // ---- notes, highlights, source guide, research clips ----------------------
    "notes.title": "Notes & Highlights",
    "notes.titleCount": "Notes & Highlights ({count})",
    "notes.research.title": "My research notes",
    "notes.toolbar.aria": "Highlight or add a note",
    "notes.toolbar.highlight": "Highlight {color}",
    "notes.toolbar.note": "Note",
    "notes.toolbar.dismiss": "Dismiss",
    "notes.toolbar.placeholder": "Your note on this passage",
    "notes.toolbar.noteAria": "Your note",
    "notes.toolbar.saveNote": "Save note",
    "notes.color.yellow": "Yellow",
    "notes.color.green": "Green",
    "notes.color.blue": "Blue",
    "notes.color.pink": "Pink",
    "notes.highlightAria": "Highlight",
    "notes.highlightWithNote": "Highlight with a note: {note}",
    "notes.filter.show": "Show",
    "notes.filter.all": "All",
    "notes.filter.highlights": "Highlights",
    "notes.filter.notes": "Notes",
    "notes.export": "Export",
    "notes.export.text": "Text",
    "notes.search": "Search your notes",
    "notes.addWhole": "Add a note about this whole material",
    "notes.add": "Add",
    "notes.loading": "Loading your notes…",
    "notes.empty": "Nothing saved yet. Select a passage while you read and tap a colour to highlight it.",
    "notes.noMatch": "Nothing matches.",
    "notes.where.page": "Page {n}",
    "notes.where.section": "Section {n}",
    "notes.where.whole": "Whole material",
    "notes.goTo": "Go to passage",
    "notes.editNote": "Edit note",
    "notes.addNote": "Add note",
    "notes.colorAria": "Colour {color}. Tap to change",
    "notes.remove": "Remove",
    "notes.keep": "Keep",
    "notes.delete": "Delete",
    "guide.title": "Source guide",
    "guide.loading": "Loading the guide…",
    "guide.writeAgain": "Write again",
    "guide.writing": "Writing…",
    "guide.summary": "Summary",
    "guide.aiNote": "Written by AI from this material. Check it against the text.",
    "guide.ask": "Get a short summary of this material and the topics worth knowing.",
    "guide.write": "Write a summary",
    "guide.writingSummary": "Writing the summary…",
    "guide.wait": "This can take a minute. You can keep reading.",
    "guide.topics": "Key topics",
    "library.card.practice": "Create 20-question practice",
    "library.card.ask": "Ask AI about this",
    "library.card.export": "Export my notes (Word)",
    "library.card.more": "More actions for {title}",
    "library.card.sections": "{count} sections",
    "library.search.label": "Search titles",
    "library.filter.group": "Material type",
    "library.filter.all": "All · {count}",
    "library.search.none": "Nothing matches.",
    "library.search.showAll": "Show everything",
    "library.continue": "Continue",
    "library.search.matchOne": "1 title matches",
    "library.search.matchMany": "{count} titles match",
    "library.eyebrow": "Learning library",
    "library.heading": "Read, pick up where you stopped, and practise",
    "library.hint": "Your own uploads, material your institute approved, and the open library, in one place.",
    "library.counts": "{titles} titles · {points} contribution points",
    "library.upload": "Upload material",
    "library.loading": "Loading the library...",
    "library.loadFailed": "The library could not be loaded. Please try again.",
    "library.empty.title": "Nothing to read yet",
    "library.empty.body": "Upload your notes in Study material. They appear here under My uploads as soon as they are processed, and your institute can approve your own work for every student.",
    "library.continueReading.title": "Continue reading",
    "library.continueReading.hint": "Saved to your account, so it follows you to any device.",
    "library.shelf.mine.title": "My uploads",
    "library.shelf.mine.hint": "Only you can read these until your institute approves them.",
    "library.shelf.coaching.title": "From my institute",
    "library.shelf.coaching.hint": "Approved by your institute for its students.",
    "library.shelf.class.title": "From my class",
    "library.shelf.class.hint": "Shared by your school with your class.",
    "library.card.shareClasses": "Share with classes",
    "library.shelf.platform.title": "Open library",
    "library.shelf.platform.hint": "Rights-cleared material published for every coaching.",
    "library.material.notes": "Notes",
    "library.material.textbook": "Textbook",
    "library.material.guide_book": "Guide book",
    "library.material.reference_book": "Reference book",
    "library.material.solved_papers": "Solved papers",
    "library.material.question_bank": "Question bank",
    "library.material.current_affairs": "Current affairs",
    "library.material.research_report": "Research report",
    "library.material.other": "Study material",
    "library.review.not_submitted": "Private",
    "library.review.pending": "Awaiting approval",
    "library.review.approved": "Approved",
    "library.review.rejected": "Not approved",
    "library.action.start": "Start reading",
    "library.action.again": "Read again",
    "library.action.continuePage": "Continue · page {page}",
    "library.queue.title": "Awaiting global publication",
    "library.queue.hint": "Own-work material a coaching has approved. Publishing makes it readable by every coaching.",
    "library.queue.empty": "Nothing is waiting. Approved material from any coaching will appear here.",
    "library.queue.publish": "Publish to open library",
    "library.queue.published": "{name} is now in the open library.",
    "library.queue.failed": "This document could not be published.",
    "library.pages.one": "1 page",
    "library.pages.many": "{count} pages",
    "library.pageNumber": "page {page}",
    "library.practiceHighlights.hint": "Turn what you marked into a 10-question practice paper.",
    "library.practiceHighlights.button": "Practise from my highlights",
    "library.practiceHighlights.busy": "Preparing questions…",
    "research.notes.button": "My notes",
    "research.notes.buttonCount": "My notes ({count})",
    "research.notes.hint": "Select text in a source or in the report, then tap a colour to keep it. Your notes go at the end of the Word report.",
    "research.notes.empty": "Nothing kept yet. Select text in a source or in the report and tap a colour.",
    "research.notes.sectionAria": "Report section",
    "research.notes.addToReport": "Add to report",
    "notes.hint": "Tip: select any words to highlight them or add a note.",
    "notes.hint.dismiss": "Got it",

    // ---- My notes dashboard and notebook page ----
    "nav.myNotes": "My notes",
    "mynotes.title": "Everything you highlighted or wrote",
    "mynotes.subtitle": "Kept by book and by research project, and organised by topic.",
    "mynotes.tab.library": "Library",
    "mynotes.tab.research": "Research",
    "mynotes.loadFailed": "Your notes could not be loaded. Please try again.",
    "mynotes.emptyLibrary.title": "No library notes yet",
    "mynotes.emptyLibrary.body": "Open a book in the Learning library, select some words and tap a colour. It shows up here, organised by topic.",
    "mynotes.emptyLibrary.cta": "Open the library",
    "mynotes.emptyResearch.title": "No research notes yet",
    "mynotes.emptyResearch.body": "In Research Studio, select text in a source or in your report and tap a colour to keep it here.",
    "mynotes.emptyResearch.cta": "Open Research Studio",
    "mynotes.menu.open": "Open notes",
    "mynotes.menu.reader": "Open the book",
    "mynotes.menu.research": "Open the project",
    "mynotes.menu.export": "Export (Word)",
    "notes.count": "{count} notes",
    "notes.count.one": "1 note",
    "notes.exportFailed": "The export could not be prepared.",
    "notebook.back": "All my notes",
    "notebook.openReader": "Open in the reader",
    "notebook.openResearch": "Open in Research Studio",
    "notebook.export": "Export",
    "notebook.notes": "Your notes",
    "notebook.general": "General",
    "notebook.guideFailed": "The summary could not be written right now.",
    "notebook.showingTopic": "Showing notes on: {topic}",
    "notebook.clearTopic": "Show all topics",
    "notebook.empty": "Nothing kept here yet. Open it and select some text to start.",
    "library.card.shared": "Shared library material",
    "library.practiceHighlights.failed": "The practice paper could not be created. Please try again.",
    "guide.moreTopics": "+{count} more",
    "guide.fewerTopics": "Show fewer",
    "notebook.topicEmpty": "No notes on this topic yet.",
    // ---- Thinkboard (tb.*) ------------------------------------------------
    "tb.save.saved": "Saved",
    "tb.save.saving": "Saving…",
    "tb.save.offline": "Offline · will retry",
    "tb.save.error": "Not saved",
    "tb.tab.board": "Board",
    "tb.tab.journey": "Journey",
    "tb.tab.slides": "Slides",
    "tb.idea.new": "New idea",
    "tb.idea.decide": "Decide…",
    "tb.link.yes": "Yes",
    "tb.toast.deletedIdea": "Deleted an idea.",
    "tb.toast.deletedIdeas": "Deleted {count} ideas.",
    "tb.toast.removedLink": "Removed a link.",
    "tb.toast.removedLinks": "Removed {count} links.",
    "tb.common.undo": "Undo",
    "tb.picture.added": "Picture added. Describe it in the Picture section for people who cannot see it.",
    "tb.picture.failed": "The picture could not be added.",
    "tb.notes.imported": "Ideas added: {ideas} · sticky notes: {notes}. Undo takes it all back in one step.",
    "tb.ai.notesAdded": "Added {count} notes beside the idea.",
    "tb.ai.noteAdded": "Added 1 note beside the idea.",
    "tb.ai.failedNothingAdded": "The AI could not answer just now. Nothing was added.",
    "tb.ai.noNewLinks": "No new links to suggest: the ideas that belong together are already linked.",
    "tb.ai.linksAdded": "Added {count} suggested links, marked ✦. Delete any you do not want.",
    "tb.ai.linkAdded": "Added 1 suggested link, marked ✦. Delete it if you do not want it.",
    "tb.ai.linksFailed": "Links could not be suggested just now.",
    "tb.ai.noQuestions": "The AI had no questions this time. Try again later.",
    "tb.ai.failed": "The AI could not answer just now.",
    "tb.board.exportFailed": "The board could not be exported.",
    "tb.board.importedWithNotes": "Imported, with notes: {notes}",
    "tb.board.imported": "Imported as a new board.",
    "tb.board.notAnExport": "That file is not a Thinkboard export (.json).",
    "tb.board.importFailed": "The board could not be imported.",
    "tb.board.cleared": "The board is clear.",
    "tb.board.opening": "Opening the board…",
    "tb.board.goneTitle": "This board could not be opened",
    "tb.board.failedTitle": "The board did not load",
    "tb.board.goneBody": "It may have been deleted, or it is no longer shared with you.",
    "tb.board.failedBody": "Check your connection, then try again. Nothing on the board is lost.",
    "tb.board.backToBoards": "Back to your boards",
    "tb.common.tryAgain": "Try again",
    "tb.board.titleLabel": "Board title",
    "tb.board.rename": "Rename",
    "tb.board.viewOnly": "View only",
    "tb.board.share": "Share",
    "tb.board.guide": "Thinkboard guide",
    "tb.board.views": "Board views",
    "tb.board.search": "Search ideas",
    "tb.board.tools": "Board tools",
    "tb.add.menu": "Add",
    "tb.add.trigger": "+ Add",
    "tb.add.idea": "Idea",
    "tb.add.decision": "Decision",
    "tb.add.note": "Sticky note",
    "tb.add.picture": "Picture",
    "tb.add.pictureHint": "Or paste one, or drop it on the board",
    "tb.add.voice": "Voice note",
    "tb.add.voiceHint": "Into the inbox",
    "tb.add.import": "Import notes",
    "tb.add.importHint": "Paste, or a .txt / .md file",
    "tb.add.spark": "Spark",
    "tb.add.sparkHint": "A prompt to get thinking",
    "tb.inbox.button": "Inbox",
    "tb.inbox.buttonAria": "Inbox, {count} waiting",
    "tb.spotlight.button": "★ Spotlight",
    "tb.spotlight.hint": "Dim everything that is not starred",
    "tb.common.redo": "Redo",
    "tb.common.undoKey": "Undo (Ctrl+Z)",
    "tb.common.redoKey": "Redo (Ctrl+Y)",
    "tb.zoom.out": "Zoom out",
    "tb.zoom.in": "Zoom in",
    "tb.zoom.level": "Zoom {percent} percent",
    "tb.zoom.fit": "Fit",
    "tb.view.menu": "View",
    "tb.view.tree": "Tidy as a tree",
    "tb.view.treeHint": "Left to right by depth; undo puts it back",
    "tb.view.mindmap": "Tidy as a mind map",
    "tb.view.mindmapHint": "Rings around the main idea",
    "tb.view.suggesting": "Suggesting links…",
    "tb.view.suggest": "Suggest links (AI)",
    "tb.view.suggestHint": "Up to four, marked ✦ · {count} AI requests left today",
    "tb.ai.noneLeft": "No AI requests left today",
    "tb.view.spotlight": "Spotlight starred ideas",
    "tb.view.leaveFocus": "Leave focus",
    "tb.view.focus": "Focus on the selected idea",
    "tb.view.replay": "Replay",
    "tb.view.replayHint": "Rebuild the board in date order",
    "tb.view.present": "Present",
    "tb.view.presentHint": "Step through the ideas; arrows to move, Esc to stop",
    "tb.view.checkin": "Weekly check-in",
    "tb.view.look": "Board look…",
    "tb.view.lookHint": "Background pattern, dark board",
    "tb.view.export": "Export the board (.json)",
    "tb.view.exportHint": "A copy you can keep or import again",
    "tb.view.importing": "Importing…",
    "tb.view.import": "Import a board (.json)",
    "tb.view.importHint": "Opens it as a new board",
    "tb.view.reset": "Reset the board…",
    "tb.board.conflict": "This board was changed in another tab or by someone else. Both sets of changes are kept; the board now shows the latest.",
    "tb.common.ok": "OK",
    "tb.board.emptyViewer": "Nothing on this board yet. Ideas appear here as soon as its owner adds them.",
    "tb.board.emptyEditor": "Double-click anywhere on the board to add your first idea, or drop a picture on it.",
    "tb.board.shown": "{shown} of {total} ideas shown",
    "tb.board.stopReview": "Stop review",
    "tb.reset.title": "Clear this board?",
    "tb.reset.confirm": "Clear the board",
    "tb.reset.body": "All {count} ideas and their links come off the board. The inbox, title and colours stay. Undo puts everything back.",
    "tb.wellbeing.title": "Before anything else",
    "tb.format.classic": "Classic slides",
    "tb.format.classicHint": "Editable slides; download as PowerPoint or a web page.",
    "tb.format.deck": "Interactive deck",
    "tb.format.deckHint": "Designed by AI: buttons, tabs, diagrams. Downloads as a web page.",
    "tb.format.page": "Single-page website",
    "tb.format.pageHint": "Designed by AI: one scrolling page. Downloads as a web page.",
    "tb.design.saving": "Saving the board…",
    "tb.design.unsaved": "Some board changes are not saved yet, so the design uses the board as it was last saved.",
    "tb.design.sending": "Sending your ideas…",
    "tb.design.phaseDeck": "The AI is designing your interactive slide deck…",
    "tb.design.phasePage": "The AI is designing your single-page website…",
    "tb.design.truncated": "Made - but the AI stopped before the end, so the last part may be missing.",
    "tb.design.readyDeck": "Your deck is ready. Change it with Refine with AI, or download it.",
    "tb.design.readyPage": "Your website is ready. Change it with Refine with AI, or download it.",
    "tb.design.failed": "The design could not be made. Nothing was saved.",
    "tb.design.closed": "The connection closed before the design was finished. Nothing was saved.",
    "tb.design.stopped": "Stopped. Nothing was saved.",
    "tb.slides.unsaved": "Some board changes are not saved yet, so the slides use the board as it was last saved.",
    "tb.slides.madeFewer": "Made {made} slides: your ideas filled {made} of the {count} asked for.",
    "tb.slides.made": "Made {count} slides.",
    "tb.slides.madeOne": "Made 1 slide.",
    "tb.slides.failed": "The slides could not be made just now. Nothing was saved.",
    "tb.slides.toolbar": "Slides",
    "tb.slides.showBuilder": "Show builder",
    "tb.slides.hideBuilder": "Hide builder",
    "tb.slides.expandAll": "Expand all",
    "tb.slides.collapseAll": "Collapse all",
    "tb.slides.pick": "Slides",
    "tb.slides.optionSlides": "{title} · {count} slides · {date}",
    "tb.slides.optionSlide": "{title} · 1 slide · {date}",
    "tb.slides.optionSections": "{title} · {count} sections · {date}",
    "tb.slides.optionSection": "{title} · 1 section · {date}",
    "tb.builder.label": "Slide builder",
    "tb.builder.ideas": "Choose ideas",
    "tb.builder.starred": "Starred ideas",
    "tb.builder.branchNamed": "“{idea}” and everything below it",
    "tb.builder.thisIdea": "This idea",
    "tb.builder.branch": "An idea and everything below it",
    "tb.builder.branchHint": "Select or focus an idea on the board first.",
    "tb.builder.whole": "The whole board",
    "tb.builder.chosen": "{count} ideas chosen.",
    "tb.builder.chosenOne": "1 idea chosen.",
    "tb.builder.promptOnly": "No ideas chosen: the slides come from your prompt alone.",
    "tb.builder.noneChosen": "No ideas chosen yet.",
    "tb.builder.prompt": "Prompt",
    "tb.builder.promptDesigned": "What should it do? (optional)",
    "tb.builder.promptClassic": "What should the slides do? (optional)",
    "tb.builder.promptPlaceholder": "For example: a 3-minute pitch to my class that ends with what we need from the teacher.",
    "tb.builder.aiOff": "AI is switched off here, so slides are built straight from your ideas.",
    "tb.builder.designs": "The AI designs it from your ideas · {count} AI requests left today.",
    "tb.builder.designsPrompt": "The AI designs it from your ideas and this prompt · {count} AI requests left today.",
    "tb.builder.noneLeftTomorrow": "No AI requests left today; they come back tomorrow.",
    "tb.builder.writes": "The AI writes the slides from your ideas and this prompt · {count} AI requests left today.",
    "tb.builder.noneLeftClear": "No AI requests left today. Clear the prompt to build slides from your ideas.",
    "tb.builder.noAi": "Leave it empty to build the slides straight from your ideas, with no AI.",
    "tb.ai.whatItSees": "What the AI sees",
    "tb.builder.size": "Size and style",
    "tb.builder.format": "Format",
    "tb.builder.howManySections": "How many sections",
    "tb.builder.howManySlides": "How many slides",
    "tb.builder.quickSizes": "Quick sizes",
    "tb.builder.range": "1 to 20.",
    "tb.builder.fewer": "Without a prompt, a small board can fill fewer.",
    "tb.builder.style": "Style",
    "tb.builder.designing": "Designing…",
    "tb.builder.writing": "Writing slides…",
    "tb.builder.making": "Making slides…",
    "tb.builder.makeWebsite": "Make the website",
    "tb.builder.make": "Make slides",
    "tb.builder.waitAi": "The AI is writing your slides. This can take up to a minute.",
    "tb.design.soFar": "{count} characters so far.",
    "tb.common.stop": "Stop",
    "tb.design.previewTitle": "The design so far",
    "tb.design.wait": "The preview appears here as soon as the page begins.",
    "tb.slides.loading": "Loading the slides…",
    "tb.common.loading": "Loading…",
    "tb.slides.loadFailed": "The slides could not be loaded.",
    "tb.slides.loadFailedHint": "Check your connection and try again; nothing is lost.",
    "tb.slides.none": "No slides yet.",
    "tb.slides.noneViewer": "Nobody has made slides from this board yet.",
    "tb.slides.noneEditor": "Choose ideas in the builder and press Make slides. Classic slides without a prompt come straight from your ideas, with no AI; an interactive deck or a website is designed by the AI.",
    "tb.style.light": "Light",
    "tb.style.dark": "Dark",
    "tb.style.vibrant": "Vibrant",
    "tb.save.unsaved": "Not saved yet",
    "tb.save.conflict": "Changed elsewhere",
    "tb.deck.saveFailed": "The slides could not be saved. Your edits are kept here; they will be sent with the next change.",
    "tb.deck.newSlide": "New slide",
    "tb.deck.backTo": "Back to: {note}.",
    "tb.deck.previousVersion": "the previous version",
    "tb.deck.undoFailed": "The last change could not be undone.",
    "tb.deck.fileFailed": "The file could not be made just now.",
    "tb.deck.printOpening": "The print window is opening: choose Save as PDF.",
    "tb.deck.printFailed": "The slides could not be prepared for printing.",
    "tb.deck.copied": "Copied. You are now editing the copy.",
    "tb.deck.copyFailed": "The slides could not be copied.",
    "tb.refine.sending": "Sending your change…",
    "tb.refine.phaseSlides": "The AI is changing your slides…",
    "tb.refine.phaseDeck": "The AI is changing your interactive slide deck…",
    "tb.refine.phasePage": "The AI is changing your single-page website…",
    "tb.refine.changedRestored": "Changed. Pictures the AI left out were kept: {count}. Undo last change takes it back.",
    "tb.refine.changed": "Changed. Undo last change takes it back.",
    "tb.refine.failed": "The change could not be made. Nothing was changed.",
    "tb.refine.closed": "The connection closed before the change was finished. Nothing was changed.",
    "tb.deck.deleted": "Slides deleted.",
    "tb.deck.deleteFailed": "The slides could not be deleted.",
    "tb.deck.titleLabel": "Deck title",
    "tb.deck.metaVisual": "{kind} · {count} {units} · {style} style",
    "tb.deck.kindWebsite": "Website",
    "tb.deck.unitSections": "sections",
    "tb.deck.unitSlides": "slides",
    "tb.deck.metaSlides": "{count} slides",
    "tb.deck.metaSlide": "1 slide",
    "tb.deck.byAi": "AI-written · check before sharing",
    "tb.deck.actions": "Deck actions",
    "tb.builder.styleLabel": "Style",
    "tb.deck.undoLast": "Undo last change",
    "tb.deck.download": "Download",
    "tb.deck.preparing": "Preparing…",
    "tb.deck.pptx": "PowerPoint (.pptx)",
    "tb.deck.pptxHint": "Edit it further in PowerPoint, Keynote or Google Slides",
    "tb.deck.html": "Web page (.html)",
    "tb.deck.htmlHint": "One file; opens in any browser, even offline",
    "tb.deck.pdf": "PDF",
    "tb.deck.pdfHint": "Opens the print window: choose Save as PDF",
    "tb.deck.more": "More deck actions",
    "tb.deck.copying": "Copying…",
    "tb.deck.copy": "Make a copy",
    "tb.deck.delete": "Delete slides",
    "tb.deck.conflict": "These slides were changed somewhere else, so your latest edits were not saved over them.",
    "tb.deck.loadLatest": "Load the latest",
    "tb.deck.changes": "Changes ({count})",
    "tb.refine.aiOff": "AI is switched off here.",
    "tb.refine.loadLatestFirst": "Load the latest version first.",
    "tb.deck.truncated": "The AI stopped before the end, so the last part may be missing. A refine or making it again can finish it.",
    "tb.deck.slidesLabel": "Slides",
    "tb.deck.addSlide": "+ Add a slide",
    "tb.deck.deleteTitle": "Delete these slides?",
    "tb.deck.deleteBody": "“{title}” will be gone for everyone who can see this board. The board and its ideas stay as they are.",
    "tb.spark.01": "What is the simplest version of this that could work?",
    "tb.spark.02": "Who would disagree with this, and why?",
    "tb.spark.03": "What would you need to believe for this to be true?",
    "tb.spark.04": "What is one thing you are assuming without checking?",
    "tb.spark.05": "If you had to start tomorrow, what would you do first?",
    "tb.spark.06": "What would make this fail?",
    "tb.spark.07": "What does success look like, exactly?",
    "tb.spark.08": "Which idea here is the riskiest? Why?",
    "tb.spark.09": "What question have you been avoiding?",
    "tb.spark.10": "How would you explain this to a ten-year-old?",
    "tb.spark.11": "What would an expert in this notice that you have missed?",
    "tb.spark.12": "What evidence would change your mind?",
    "tb.spark.13": "Which two ideas here could be combined?",
    "tb.spark.14": "What is the opposite of your main idea, and is any of it right?",
    "tb.spark.15": "What would you do with twice the time? With half?",
    "tb.spark.16": "Who else has solved a problem like this?",
    "tb.spark.17": "What is the smallest experiment you could run this week?",
    "tb.spark.18": "What would you cut if you could keep only three ideas?",
    "tb.spark.19": "What is the cause, and what is only a symptom?",
    "tb.spark.20": "Where could this go wrong for someone else?",
    "tb.spark.21": "What do you already know for certain here?",
    "tb.spark.22": "What would make this ten times better, not ten percent?",
    "tb.spark.23": "Which idea are you most excited about, and is that a good reason?",
    "tb.spark.24": "What happens next if this works?",
    "tb.spark.25": "What did you learn that surprised you?",
    "tb.spark.26": "What is missing from this board?",
    "tb.spark.27": "If a friend showed you this, what would you ask them?",
    "tb.spark.28": "Which link between two ideas is the weakest?",
    "tb.spark.29": "What would a sceptic need to see?",
    "tb.spark.30": "What can you measure to know you are on track?",
    "tb.spark.31": "What decision are you putting off?",
    "tb.spark.32": "What does this look like from the user's side?",
    "tb.spark.33": "Which constraint is real, and which have you only assumed?",
    "tb.spark.34": "What story does this board tell, from start to finish?",
    "tb.spark.35": "What is one idea you dismissed too quickly?",
    "tb.spark.36": "What would you do differently if you started again?",
    "tb.spark.label": "Spark",
    "tb.spark.fromAi": "Spark · from your board (AI)",
    "tb.spark.asking": "Asking the AI…",
    "tb.spark.ask": "Ask AI for 3 from this board",
    "tb.common.close": "Close",
    "tb.spark.another": "Another",
    "tb.spark.add": "Add as note",
    "tb.help.back": "← Your boards",
    "tb.help.title": "Thinkboard guide",
    "tb.help.lead": "A board is a space to think: ideas, links between them, and the story they tell - then slides to share it.",
    "tb.help.ideas.h": "Ideas and links",
    "tb.help.ideas.1": "**Add an idea:** double-click empty space, or use *+ Add* (idea, decision, sticky note, picture).",
    "tb.help.ideas.2": "**Link two ideas:** drag the small handle on an idea onto another, or press *Connect* in the side panel and click the other idea.",
    "tb.help.ideas.3": "**Colour, shape, star, pros and cons, date, group, picture:** select an idea; the side panel has them all.",
    "tb.help.ideas.4": "**Find things:** search at the top, tap colours in the legend, or *Spotlight* to see only starred ideas.",
    "tb.help.ideas.5": "Every change saves itself. *Undo* (Ctrl+Z) and *Redo* (Ctrl+Y) work for every step.",
    "tb.help.keys.h": "Keys",
    "tb.help.keys.arrows": "Arrow keys",
    "tb.help.keys.arrowsDo": "Move between ideas",
    "tb.help.keys.alt": "Alt + arrows",
    "tb.help.keys.altDo": "Move the selected idea",
    "tb.help.keys.enter": "Enter",
    "tb.help.keys.enterDo": "Edit the selected idea",
    "tb.help.keys.delete": "Delete",
    "tb.help.keys.deleteDo": "Remove the selected idea or link (a message offers Undo)",
    "tb.help.keys.esc": "Esc",
    "tb.help.keys.escDo": "Cancel, or clear the selection",
    "tb.help.keys.undo": "Ctrl + Z / Ctrl + Y",
    "tb.help.keys.undoDo": "Undo / redo",
    "tb.help.capture.h": "Catching thoughts",
    "tb.help.capture.1": "**Inbox:** jot a thought now, place it on the board later. A voice note becomes text in the inbox.",
    "tb.help.capture.2": "**Import notes:** paste notes or open a .txt / .md file; headings and bullets become linked ideas.",
    "tb.help.capture.3": "**Spark:** a question to get you thinking when you are stuck.",
    "tb.help.views.h": "Seeing the board differently",
    "tb.help.views.1": "**Journey:** the ideas by date. **Present:** step through them. **Replay:** watch the board grow.",
    "tb.help.views.2": "**Tidy as a tree / mind map** (View menu) lays the ideas out; Undo puts them back.",
    "tb.help.views.3": "**Weekly check-in** shows ideas that have gone quiet.",
    "tb.help.slides.h": "Slides",
    "tb.help.slides.1": "Open the *Slides* tab, choose ideas (starred, an idea and everything below it, or the whole board), and press *Make slides*.",
    "tb.help.slides.2": "**Classic slides** without a prompt are built straight from your ideas, with no AI. You can edit every card, and download PowerPoint, a web page or a PDF.",
    "tb.help.slides.3": "**Interactive deck** and **website** are designed by the AI and download as one web page.",
    "tb.help.slides.4": "**Refine with AI** changes a deck in one step; *Undo last change* takes it back.",
    "tb.help.sharing.h": "Sharing",
    "tb.help.sharing.1": "Press *Share* on a board. Make a view-only or an editing link and send it to classmates - links only work for people at your institute.",
    "tb.help.sharing.2": "Staff can share a board with everyone at the institute (view only).",
    "tb.help.sharing.3": "The owner sees who joined and can remove anyone, or a link. Someone a board was shared with can leave it.",
    "tb.help.ai.h": "AI on Thinkboard - what it sees",
    "tb.help.ai.1": "When you ask for AI help (Think with AI, Suggest links, slides with a prompt, designed decks, Refine), the text of **this board only** is sent to the AI model your institute set up - never your other boards, never your pictures (a picture is only named as \"picture 1\").",
    "tb.help.ai.2": "The AI's words are suggestions. Check them before you rely on them or share them; downloads made by AI say so.",
    "tb.help.ai.3": "Each person has a daily number of AI requests; the side panel and the slide builder show how many are left.",
    "tb.help.ai.4": "If something you write suggests you may be in difficulty, the AI is not asked; you are shown where to get help.",
    "tb.help.ai.5": "Your institute can switch Thinkboard's AI off for everyone there. Everything else keeps working.",
    "tb.help.ai.6": "The platform can also answer students who may be under 18 with the AI model on its own server, so their words do not go to an outside AI service.",
    "tb.help.data.h": "Your data",
    "tb.help.data.1": "Boards, pictures and slides are stored for your institute and are visible only to the people they are shared with.",
    "tb.help.data.2": "A deleted board is kept for **90 days** so your institute can bring it back, then removed for good with its pictures and slides. Deleted slides are kept for **30 days**. A picture no idea shows any more is removed after **7 days** (so Undo can still bring it back).",
    "tb.help.data.3": "Activity logs record what happened (a board was saved, a deck was made) and how many - never what you wrote.",
    "tb.help.data.4": "If you are under 18, please use Thinkboard with your parent or guardian's knowledge, as you would any study app; your institute can tell you how your data is handled.",
    "tb.help.data.5": "Download any board as a .json file from the View menu to keep your own copy.",
    "tb.think.challenge": "Challenge it",
    "tb.think.challengeHint": "Questions that test it",
    "tb.think.missing": "What am I missing?",
    "tb.think.missingHint": "Gaps to fill",
    "tb.think.variations": "Five variations",
    "tb.think.variationsHint": "Other versions of it",
    "tb.think.devil": "Devil's advocate",
    "tb.think.devilHint": "The case against",
    "tb.shape.rect": "Idea",
    "tb.shape.pill": "Pill",
    "tb.shape.circle": "Circle",
    "tb.shape.diamond": "Decision",
    "tb.shape.note": "Sticky note",
    "tb.confidence.0": "Not rated",
    "tb.confidence.1": "Guess",
    "tb.confidence.2": "Likely",
    "tb.confidence.3": "Proven",
    "tb.inspector.idea": "Idea",
    "tb.inspector.link": "Link",
    "tb.inspector.text": "Text",
    "tb.inspector.shape": "Shape",
    "tb.inspector.colour": "Colour · {label}",
    "tb.inspector.reactions": "Reactions",
    "tb.inspector.react": "React {emoji}",
    "tb.inspector.reactCount": "React {emoji}, {count} so far",
    "tb.inspector.removeOne": "Remove one {emoji}",
    "tb.inspector.starred": "★ Starred",
    "tb.inspector.star": "★ Star",
    "tb.inspector.branch": "Branch",
    "tb.inspector.connect": "Connect",
    "tb.inspector.focus": "Focus",
    "tb.common.delete": "Delete",
    "tb.think.title": "Think with AI",
    "tb.think.intro": "Short notes appear beside the idea, linked to it. One undo takes them all back.",
    "tb.think.thinking": "Thinking…",
    "tb.think.writeFirst": "Write something on the idea first.",
    "tb.think.noneLeft": "No AI requests left today. They come back tomorrow.",
    "tb.think.left": "{remaining} of {limit} AI requests left today.",
    "tb.think.onlyThisBoard": "Only this board's text is sent.",
    "tb.weigh.title": "Weigh it",
    "tb.weigh.pros": "Pros (one per line)",
    "tb.weigh.cons": "Cons (one per line)",
    "tb.weigh.sure": "How sure are you?",
    "tb.details.title": "Date and group",
    "tb.details.date": "Date",
    "tb.details.group": "Group",
    "tb.details.groupHint": "Ideas with the same group name are drawn together, and the group can be folded away.",
    "tb.picture.title": "Picture",
    "tb.link.linksTo": "links to",
    "tb.link.byAi": "Suggested by AI. Keep it, relabel it, or delete it.",
    "tb.link.label": "Label",
    "tb.link.labelPlaceholder": "e.g. because, leads to, Yes",
    "tb.link.reverse": "Reverse",
    "tb.link.delete": "Delete link",
    "tb.picture.alt": "Picture on this idea",
    "tb.picture.loading": "Loading the picture…",
    "tb.picture.uploading": "Uploading…",
    "tb.picture.replace": "Replace picture",
    "tb.picture.add": "Add a picture",
    "tb.common.remove": "Remove",
    "tb.picture.orPaste": "Or paste a picture, or drop one on the board.",
    "tb.picture.describe": "Describe the picture",
    "tb.picture.describePlaceholder": "For people who cannot see it",
    "tb.picture.describeHint": "A short description helps screen-reader users and anyone on a slow connection.",
    "tb.role.owner": "Yours",
    "tb.role.editor": "Shared · can edit",
    "tb.role.viewer": "Shared · view only",
    "tb.home.untitled": "Untitled board",
    "tb.home.createFailed": "The board could not be created.",
    "tb.home.tooLarge": "That file is too large to be a Thinkboard export.",
    "tb.home.imported": "Board imported.",
    "tb.home.notJson": "That file is not valid JSON.",
    "tb.home.renameFailed": "The board could not be renamed.",
    "tb.home.open": "Open",
    "tb.home.duplicate": "Duplicate",
    "tb.home.duplicateHint": "A private copy for you",
    "tb.home.copiedAs": "Copied as \"{title}\".",
    "tb.home.copyFailed": "The board could not be copied.",
    "tb.home.exportJson": "Export as JSON",
    "tb.home.heading": "Think it through, then show it",
    "tb.home.lead": "Capture ideas, connect and weigh them, then turn the board into slides or a page.",
    "tb.home.import": "Import",
    "tb.home.newTitle": "New board title",
    "tb.home.newPlaceholder": "Name a new board, e.g. Science fair idea",
    "tb.home.creating": "Creating…",
    "tb.home.new": "+ New board",
    "tb.home.loading": "Loading your boards…",
    "tb.home.loadFailed": "Your boards could not be loaded.",
    "tb.home.none": "No boards yet",
    "tb.home.noneHint": "Name a board above and start with one idea. Double-click anywhere on the board to add the next.",
    "tb.home.list": "Your boards",
    "tb.home.ideas": "{count} ideas · {date}",
    "tb.home.idea": "1 idea · {date}",
    "tb.home.sharedAll": "Shared with everyone",
    "tb.home.actionsFor": "Actions for {title}",
    "tb.home.renameTitle": "Rename board",
    "tb.common.cancel": "Cancel",
    "tb.common.save": "Save",
    "tb.home.deleteTitle": "Delete this board?",
    "tb.home.deleteConfirm": "Delete board",
    "tb.home.deleteBody": "“{title}” leaves your boards, and anyone it was shared with loses it too. Ask your institute's admin if you need it back.",
    "tb.home.deleted": "Board deleted.",
    "tb.home.deleteFailed": "The board could not be deleted.",
    "tb.share.role.owner": "Owner",
    "tb.share.role.editor": "Can edit",
    "tb.share.role.viewer": "View only",
    "tb.share.instituteOn": "Everyone at your institute can now view this board.",
    "tb.share.instituteOff": "This board is no longer shared with everyone.",
    "tb.share.instituteFailed": "Sharing could not be changed.",
    "tb.share.linkFailed": "The share link could not be made.",
    "tb.share.copied": "Link copied. Paste it to a classmate at your institute.",
    "tb.share.ready": "Link ready. Select it in the box and copy it.",
    "tb.share.revoked": "That link no longer opens the board. People who already joined keep their access.",
    "tb.share.revokeFailed": "The share link could not be removed.",
    "tb.share.removed": "{name} no longer has this board.",
    "tb.share.removeFailed": "That person could not be removed.",
    "tb.share.left": "You left the board.",
    "tb.share.leaveFailed": "You could not leave the board just now.",
    "tb.share.title": "Share “{title}”",
    "tb.share.byInstitute": "Your institute shared this board with everyone. You can view it.",
    "tb.share.withYouEditor": "It was shared with you: can edit.",
    "tb.share.withYouViewer": "It was shared with you: view only.",
    "tb.share.leave": "Leave this board",
    "tb.share.everyone": "Everyone at your institute",
    "tb.share.everyoneCheck": "Every student and teacher here can view it (not change it).",
    "tb.share.staffOnly": "Only staff can share with the whole institute. Share a link with your classmates instead.",
    "tb.share.links": "Share links",
    "tb.share.linksHint": "A link works only for people at your institute. Anyone who opens it joins the board.",
    "tb.share.newViewer": "New view-only link",
    "tb.share.newEditor": "New editing link",
    "tb.share.linksFailed": "The links could not be loaded. Close and open this again.",
    "tb.share.made": "{role} · made {date}",
    "tb.share.linkLabel": "{role} link",
    "tb.share.copiedButton": "Copied",
    "tb.share.copy": "Copy",
    "tb.share.removeViewerLink": "Remove the view only link",
    "tb.share.removeEditorLink": "Remove the editing link",
    "tb.share.noLinks": "No links yet.",
    "tb.share.people": "People",
    "tb.share.peopleFailed": "The people on this board could not be loaded.",
    "tb.share.peopleList": "People on this board",
    "tb.share.removePerson": "Remove {name}",
    "tb.share.nobody": "Nobody has joined through a link yet.",
    "tb.event.deck_classic_auto": "Classic slides (no AI)",
    "tb.event.deck_classic_ai": "Classic slides (AI)",
    "tb.event.deck_deck": "Interactive decks",
    "tb.event.deck_page": "Websites",
    "tb.event.refine_classic": "Refines (classic)",
    "tb.event.refine_design": "Refines (designed)",
    "tb.event.export_pptx": "PowerPoint downloads",
    "tb.event.export_html": "Web page downloads",
    "tb.event.export_design_html": "Designed page downloads",
    "tb.event.board_export": "Board exports",
    "tb.event.board_import": "Board imports",
    "tb.feature.thinkboard_idea": "Think with AI",
    "tb.feature.thinkboard_links": "Suggest links",
    "tb.feature.thinkboard_deck": "Slides with a prompt",
    "tb.feature.thinkboard_design": "Designed decks",
    "tb.feature.thinkboard_refine": "Refine",
    "tb.feature.thinkboard_spark": "Spark from the board",
    "tb.institute.aiOn": "Thinkboard's AI is on for your institute.",
    "tb.institute.aiOff": "Thinkboard's AI is off for your institute. Everything else keeps working.",
    "tb.institute.saveFailed": "The setting could not be saved.",
    "tb.institute.title": "For your institute (admins)",
    "tb.institute.aiLabel": "Thinkboard's AI for everyone here",
    "tb.institute.aiHint": "Off: no AI suggestions, AI slides or refine on Thinkboard. Boards, slides without a prompt and downloads keep working.",
    "tb.institute.platformOff": "The platform has switched Thinkboard's AI off for everyone, so this has no effect for now.",
    "tb.institute.counts": "Last 30 days, counts only",
    "tb.institute.countsFailed": "The counts could not be loaded.",
    "tb.institute.boardsLive": "Boards in use",
    "tb.institute.boardsNew": "New boards",
    "tb.institute.decks": "Saved decks",
    "tb.institute.activity": "Slides, refines, downloads",
    "tb.institute.noneYet": "none yet",
    "tb.institute.failed": "{count} failed",
    "tb.institute.aiRequests": "AI requests",
    "tb.chip.icons": "Icons",
    "tb.chip.iconsHint": "Add simple inline SVG icons to the headings and key points.",
    "tb.chip.chart": "A chart",
    "tb.chip.chartHint": "Add one simple inline SVG chart that uses only numbers already in it; if it has no numbers, add a diagram instead.",
    "tb.chip.visual": "More visual",
    "tb.chip.visualHint": "Make it more visual: fewer words, bigger shapes, a diagram where it helps.",
    "tb.chip.tabs": "Tabs or accordions",
    "tb.chip.tabsHint": "Turn the densest part into tabs or accordions.",
    "tb.chip.shorter": "Shorter text",
    "tb.chip.shorterHint": "Make all the text shorter and punchier; keep every idea.",
    "tb.chip.bolder": "Bolder colours",
    "tb.chip.bolderHint": "Use bolder colours and stronger contrast, still readable.",
    "tb.chip.stats": "Stats section",
    "tb.chip.statsHint": "Add a short stats or key-facts section using only facts already in it.",
    "tb.chip.expand": "Expand/collapse sections",
    "tb.chip.expandHint": "Make the long sections expand and collapse, closed at first.",
    "tb.chip.shorterBullets": "Shorter text",
    "tb.chip.shorterBulletsHint": "Make every bullet shorter; keep every idea.",
    "tb.chip.simpler": "Simpler words",
    "tb.chip.simplerHint": "Use simpler words a younger student would understand.",
    "tb.chip.notes": "Better speaker notes",
    "tb.chip.notesHint": "Rewrite the speaker notes so they are easy to say aloud.",
    "tb.chip.summary": "Add a summary slide",
    "tb.chip.summaryHint": "Add a short summary slide at the end.",
    "tb.chip.persuasive": "More persuasive",
    "tb.chip.persuasiveHint": "Make it more persuasive, ending with a clear ask.",
    "tb.refine.title": "Refine with AI",
    "tb.refine.quick": "Quick changes",
    "tb.refine.what": "What should change?",
    "tb.refine.placeholder": "What should change? For example: put the robot first and add a question for the class.",
    "tb.refine.changing": "Changing…",
    "tb.refine.change": "Change it",
    "tb.refine.undoable": "Each change can be undone with Undo last change.",
    "tb.slide.previewOf": "Slide {number} preview: {title}",
    "tb.slide.untitledLower": "untitled",
    "tb.slide.untitled": "Untitled slide",
    "tb.slide.titleSlide": "Title slide",
    "tb.slide.numbered": "Slide {number}",
    "tb.slide.tools": "{name} tools",
    "tb.slide.title": "Title",
    "tb.slide.subtitle": "Subtitle lines",
    "tb.slide.bullets": "Bullets",
    "tb.slide.onePerLine": "One per line.",
    "tb.slide.notes": "Speaker notes",
    "tb.slide.unpicture": "Take the picture off this slide",
    "tb.present.label": "Presenting",
    "tb.present.exit": "Exit",
    "tb.present.previous": "Previous idea",
    "tb.present.position": "{index} of {total}",
    "tb.idea.untitled": "Untitled idea",
    "tb.present.next": "Next idea",
    "tb.replay.label": "Replaying",
    "tb.replay.progress": "{shown} of {total} ideas",
    "tb.checkin.newThisWeek": "{count} new ideas this week",
    "tb.checkin.newThisWeekOne": "{count} new idea this week",
    "tb.checkin.settled": "{count} settled (proven, or marked ✅)",
    "tb.checkin.stale": "{count} untouched for {days}+ days",
    "tb.checkin.review": "Review stale",
    "tb.checkin.reviewing": "Showing only the quiet ideas. Edit one to bring it back.",
    "tb.import.chooseFile": "Choose a .txt or .md file.",
    "tb.import.tooLarge": "That file is too large for notes (1 MB at most).",
    "tb.import.title": "Import notes",
    "tb.import.howTo": "# Headings become main ideas, bullets and numbered items become linked ideas under them (indentation sets the level), and plain paragraphs become sticky notes.",
    "tb.import.paste": "Paste your notes",
    "tb.import.placeholder": "# Science fair\n- Volcano model\n  - Baking soda version\n- Line-following robot\n\nAsk the teacher about the budget.",
    "tb.import.openFile": "Open a .txt or .md file",
    "tb.import.count": "Ideas: {ideas} · links: {links} · sticky notes: {notes}",
    "tb.import.nothing": "Nothing to import yet.",
    "tb.import.add": "Add to board",
    "tb.legend.0": "Idea",
    "tb.legend.1": "Question",
    "tb.legend.2": "Decision",
    "tb.legend.3": "Risk",
    "tb.legend.4": "Evidence",
    "tb.legend.5": "Action",
    "tb.legend.6": "Parked",
    "tb.inbox.close": "Close the inbox",
    "tb.inbox.label": "A thought to keep for later",
    "tb.inbox.placeholder": "Type a thought, press Enter",
    "tb.inbox.empty": "Nothing waiting. Thoughts you catch here wait until you place them on the board.",
    "tb.inbox.voiceNote": "Voice note",
    "tb.inbox.place": "Put on the board: {text}",
    "tb.inbox.toBoard": "→ Board",
    "tb.inbox.remove": "Remove: {text}",
    "tb.voice.notSetUp": "Voice notes are not set up on this server. Type the note instead.",
    "tb.voice.nothingHeard": "Nothing was heard. Try again, a little closer to the microphone.",
    "tb.voice.failed": "The voice note could not be turned into text. Type it instead.",
    "tb.voice.unsupported": "This browser cannot record. Type the note instead.",
    "tb.voice.writing": "Writing it down…",
    "tb.voice.stop": "● Stop ({seconds}s)",
    "tb.voice.start": "🎤 Voice note",
    "tb.voice.blocked": "The microphone is blocked. Allow it in the browser's site settings, or type the note.",
    "tb.bg.dots": "Dots",
    "tb.bg.grid": "Grid",
    "tb.bg.ruled": "Ruled lines",
    "tb.bg.plain": "Plain",
    "tb.bg.blueprint": "Blueprint",
    "tb.bg.chalkboard": "Chalkboard",
    "tb.bg.paper": "Paper",
    "tb.bg.midnight": "Midnight",
    "tb.bg.sunset": "Sunset",
    "tb.bg.ocean": "Ocean",
    "tb.look.title": "Board look",
    "tb.look.background": "Background",
    "tb.look.dark": "Dark board",
    "tb.look.darkHint": "Dark behind any pattern; saved with the board, for everyone who opens it.",
    "tb.common.done": "Done",
    "tb.legend.label": "Colour legend",
    "tb.legend.colours": "Colours",
    "tb.legend.showAll": "Show all",
    "tb.legend.filterBy": "Filter by {label}",
    "tb.legend.only": "Show only ideas of this colour",
    "tb.legend.count": "{count} ideas",
    "tb.legend.name": "Name of colour {number}",
    "tb.join.incomplete": "This share link is incomplete.",
    "tb.join.failed": "This share link could not be opened.",
    "tb.join.title": "This share link did not open a board",
    "tb.join.sameInstitute": "{problem} Links work only for students and staff of the same institute.",
    "tb.join.goToBoards": "Go to your boards",
    "tb.join.opening": "Opening the shared board…",
    "tb.journey.empty": "Nothing to show yet",
    "tb.journey.filtered": "Every idea is filtered out. Clear the search or the colour filter to see the journey.",
    "tb.journey.addIdeas": "Add ideas on the board; each one is placed here under its date.",
    "tb.journey.noDate": "No date",
    "tb.journey.cardAria": "{text}. Open it on the board",
    "tb.journey.cardAriaStarred": "{text}, starred. Open it on the board",
    "tb.journey.cardAriaDecision": "{text}, decision. Open it on the board",
    "tb.journey.cardAriaStarredDecision": "{text}, starred, decision. Open it on the board",
    "tb.journey.from": "from “{idea}”",
    "tb.journey.start": "A starting point",
    "tb.journey.leadsTo": "leads to {count} ideas",
    "tb.journey.leadsToOne": "leads to 1 idea",
    "tb.group.open": "Group {name}, {count} ideas. Fold it away",
    "tb.group.folded": "Group {name}, {count} ideas folded. Open it",
    "tb.canvas.label": "Board: {title}. Double-click empty space to add an idea. Arrow keys move between ideas, Alt+arrows move the selected idea, Enter edits it, Delete removes it.",
    "tb.canvas.linkHint": "Click the idea to link to · Esc to cancel",
    "tb.node.empty": "Empty idea",
    "tb.node.starred": "starred",
    "tb.node.filtered": "filtered out",
    "tb.node.text": "Idea text",
    "tb.node.prosCons": "Pros and cons",
    "tb.node.confidence": "Confidence",
    "tb.node.handle": "Drag to another idea to link them",
    "tb.save.rejected": "Some changes could not be saved and were undone.",
    "tb.save.forbidden": "You can no longer change this board.",
    "tb.picture.unsupported": "Use a PNG, JPEG, WebP or GIF picture.",
    "tb.preview.blocked": "This design keeps trying to open another page, so it is not shown. Undo the last change, or make it again.",
    "tb.preview.escaped": "This design tried to open another page. That is blocked here.",
    "tb.preview.title": "Preview: {title}",
    "tb.preview.full": "Full screen",
    "tb.preview.fullTitle": "{title}, full screen",
    "tb.preview.close": "Close (Esc)",
    "tb.preview.fullFrame": "Full screen: {title}",
    "tb.slide.moveUp": "Move slide {number} up",
    "tb.slide.moveDown": "Move slide {number} down",
    "tb.slide.delete": "Delete slide {number}",
    "tb.slide.moveUpTitle": "Move title slide up",
    "tb.slide.moveDownTitle": "Move title slide down",
    "tb.slide.deleteTitle": "Delete title slide",
    // ---- end of Thinkboard ----------------------------------------------
} as const;

export type TranslationKey = keyof typeof en;
export type Dictionary = Partial<Record<TranslationKey, string>>;

/**
 * Stated plainly so the UI can be honest about it rather than presenting a
 * half-translated app as fully localised.
 */
export const TRANSLATION_SCOPE_NOTE =
    "Student screens are translated. Admin and management screens are still English.";
